import { Alignment, Content } from 'pdfmake/interfaces';

function toStringValue(input: unknown): string {
  return typeof input === 'string' ? input : '';
}

const richTextBlockSelector = [
  'p',
  'div',
  'section',
  'article',
  'header',
  'footer',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
].join(',');

export function sanitizeRichTextHtml(input: unknown): string {
  const value = toStringValue(input);
  const parser = new DOMParser();
  const doc = parser.parseFromString(value || '', 'text/html');

  doc.querySelectorAll('script,style,iframe,object,embed').forEach((el) => el.remove());

  doc.querySelectorAll('*').forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      const attrName = attr.name.toLowerCase();
      const attrValue = (attr.value || '').toLowerCase();
      const isEventHandler = attrName.startsWith('on');
      const isJsUrl = (attrName === 'href' || attrName === 'src') && attrValue.includes('javascript:');

      if (isEventHandler || isJsUrl) {
        el.removeAttribute(attr.name);
      }
    });
  });

  const cleaned = (doc.body.innerHTML || '').trim();
  const textOnly = (doc.body.textContent || '').replace(/\u00a0/g, ' ').trim();

  return textOnly ? cleaned : '';
}

export function normalizeRichTextHtml(input: unknown): string {
  const sanitized = sanitizeRichTextHtml(input);
  if (!sanitized) {
    return '';
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitized, 'text/html');
  if (doc.body.querySelector(richTextBlockSelector)) {
    return sanitized;
  }

  const normalized = sanitized.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!normalized) {
    return '';
  }

  return normalized
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

export function sectionedTextToRichTextHtml(input: unknown): string {
  const value = toStringValue(input);
  if (!value.trim()) {
    return '';
  }

  const normalizedExistingHtml = normalizeRichTextHtml(value);
  if (normalizedExistingHtml && normalizedExistingHtml !== normalizeRichTextHtml(extractHtmlText(value))) {
    return normalizedExistingHtml;
  }

  const lines = value
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => line.trim());

  const blocks: string[] = [];
  let paragraphLines: string[] = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) {
      return;
    }

    blocks.push(`<p>${paragraphLines.map(escapeHtml).join('<br>')}</p>`);
    paragraphLines = [];
  };

  lines.forEach((line) => {
    if (!line) {
      flushParagraph();
      return;
    }

    const sectionMatch = /^(\d+\.\s+.+)$/.exec(line);
    if (sectionMatch) {
      flushParagraph();
      blocks.push(`<h4>${escapeHtml(sectionMatch[1])}</h4>`);
      return;
    }

    const isShortLabel = /^[A-Za-z][A-Za-z0-9/()'.,\-\s]+:$/.test(line) && line.length <= 90;
    if (isShortLabel) {
      flushParagraph();
      blocks.push(`<p><strong>${escapeHtml(line)}</strong></p>`);
      return;
    }

    paragraphLines.push(line);
  });

  flushParagraph();

  return normalizeRichTextHtml(blocks.join(''));
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function extractHtmlText(input: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(input || '', 'text/html');
  return doc.body.textContent || '';
}

type PdfInlineRun =
  | string
  | {
      text: string;
      bold?: boolean;
      italics?: boolean;
      decoration?: 'underline';
      color?: string;
    };

function applyInlineStyle(
  runs: PdfInlineRun[],
  style: Partial<Extract<PdfInlineRun, { text: string }>>
): PdfInlineRun[] {
  return runs.map((run) => {
    if (typeof run === 'string') {
      return { text: run, ...style };
    }
    return { ...run, ...style };
  });
}

function normalizeRuns(runs: PdfInlineRun[]): PdfInlineRun[] {
  const out: PdfInlineRun[] = [];
  let pendingSpace = false;

  const pushString = (value: string) => {
    const prev = out[out.length - 1];
    if (typeof prev === 'string') {
      out[out.length - 1] = prev + value;
      return;
    }
    out.push(value);
  };

  const pushNewline = () => {
    const last = out[out.length - 1];
    if (last !== '\n') {
      pushString('\n');
    }
    pendingSpace = false;
  };

  runs.forEach((run) => {
    const raw = (typeof run === 'string' ? run : run.text)
      .replace(/\u00a0/g, ' ')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n');

    raw.split(/(\n+)/).forEach((part) => {
      if (!part) {
        return;
      }

      if (part.includes('\n')) {
        pushNewline();
        return;
      }

      const normalized = part.replace(/[ \t\f\v]+/g, ' ');
      const startsWithSpace = normalized.startsWith(' ');
      const endsWithSpace = normalized.endsWith(' ');
      const core = normalized.trim();

      if (!core) {
        if (out.length > 0) {
          pendingSpace = true;
        }
        return;
      }

      const prefix = (startsWithSpace || pendingSpace) && out.length > 0 ? ' ' : '';
      const finalText = `${prefix}${core}`;

      if (typeof run === 'string') {
        pushString(finalText);
      } else {
        out.push({ ...run, text: finalText });
      }

      pendingSpace = endsWithSpace;
    });
  });

  return out;
}

function parseInlineNode(node: Node): PdfInlineRun[] {
  if (node.nodeType === Node.TEXT_NODE) {
    return [node.textContent || ''];
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }

  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  if (tag === 'br') {
    return ['\n'];
  }

  let runs: PdfInlineRun[] = [];
  el.childNodes.forEach((child) => {
    runs = runs.concat(parseInlineNode(child));
  });

  if (tag === 'strong' || tag === 'b') {
    return applyInlineStyle(runs, { bold: true });
  }

  if (tag === 'em' || tag === 'i') {
    return applyInlineStyle(runs, { italics: true });
  }

  if (tag === 'u') {
    return applyInlineStyle(runs, { decoration: 'underline' });
  }

  if (tag === 'a') {
    return applyInlineStyle(runs, { decoration: 'underline', color: '#1a73e8' });
  }

  return runs;
}

function extractPlainTextFromHtml(input: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(input || '', 'text/html');
  const raw = (doc.body.textContent || '').replace(/\u00a0/g, ' ');
  return raw.replace(/\s+/g, ' ').trim();
}

function toPdfParagraph(
  runs: PdfInlineRun[],
  style: string,
  alignment: Alignment,
  margin: [number, number, number, number]
): Content {
  const normalized = normalizeRuns(runs);
  if (!normalized.length) {
    return { text: '', style, alignment, margin };
  }

  return {
    text: normalized,
    style,
    alignment,
    margin,
  };
}

type PdfStackOptions = {
  style?: string;
  alignment?: Alignment;
  margin?: [number, number, number, number];
  flow?: 'block' | 'inline';
};

export function richTextToPdfStack(input: unknown, options?: PdfStackOptions): Content {
  const style = options?.style || 'normalText';
  const alignment = options?.alignment || 'justify';
  const margin = options?.margin || [0, 0, 0, 6];
  const flow = options?.flow || 'block';

  const html = toStringValue(input);
  if (!html) {
    return { text: '', style, alignment };
  }

  const sanitized = normalizeRichTextHtml(html);
  if (!sanitized) {
    return { text: '', style, alignment };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitized, 'text/html');

  if (flow === 'inline') {
    let inlineRuns: PdfInlineRun[] = [];

    const appendSeparator = () => {
      const last = inlineRuns[inlineRuns.length - 1];
      if (last === ' ' || last === '\n') {
        return;
      }
      inlineRuns.push(' ');
    };

    doc.body.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = (node.textContent || '').trim();
        if (text) {
          if (inlineRuns.length) {
            appendSeparator();
          }
          inlineRuns.push(text);
        }
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();

      if (inlineRuns.length) {
        appendSeparator();
      }

      if (tag === 'ul') {
        Array.from(el.querySelectorAll(':scope > li')).forEach((li, index) => {
          if (index > 0) {
            appendSeparator();
          }
          inlineRuns.push('• ');
          inlineRuns = inlineRuns.concat(parseInlineNode(li));
        });
        return;
      }

      if (tag === 'ol') {
        Array.from(el.querySelectorAll(':scope > li')).forEach((li, index) => {
          if (index > 0) {
            appendSeparator();
          }
          inlineRuns.push(`${index + 1}. `);
          inlineRuns = inlineRuns.concat(parseInlineNode(li));
        });
        return;
      }

      inlineRuns = inlineRuns.concat(parseInlineNode(el));
    });

    const normalizedInline = normalizeRuns(inlineRuns);
    return {
      text: normalizedInline.length ? normalizedInline : '',
      style,
      alignment,
      margin,
    };
  }

  const blocks: Content[] = [];

  const pushParagraphFromNode = (node: Node, prefix?: string) => {
    let runs = parseInlineNode(node);
    if (prefix) {
      runs = [prefix, ...runs];
    }
    const textOnly = runs
      .map((run) => (typeof run === 'string' ? run : run.text))
      .join('')
      .trim();

    if (!textOnly) {
      return;
    }

    blocks.push(toPdfParagraph(runs, style, alignment, margin));
  };

  const blockTags = new Set([
    'p',
    'div',
    'section',
    'article',
    'header',
    'footer',
    'blockquote',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
  ]);

  doc.body.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const value = (node.textContent || '').trim();
      if (value) {
        blocks.push(toPdfParagraph([value], style, alignment, margin));
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === 'ul') {
      Array.from(el.querySelectorAll(':scope > li')).forEach((li) => {
        pushParagraphFromNode(li, '• ');
      });
      return;
    }

    if (tag === 'ol') {
      Array.from(el.querySelectorAll(':scope > li')).forEach((li, index) => {
        pushParagraphFromNode(li, `${index + 1}. `);
      });
      return;
    }

    if (blockTags.has(tag)) {
      pushParagraphFromNode(el);
      return;
    }

    pushParagraphFromNode(el);
  });

  if (!blocks.length) {
    const fallback = extractPlainTextFromHtml(sanitized);
    return { text: fallback, style, alignment };
  }

  return { stack: blocks };
}
